import React, { useEffect, useState } from 'react'
import { getGpaPercentAverages, getOverviewTexts, saveOverviewTexts } from '../../../lib/studentcourselessonreport'
import { convertToFourPointScale } from '../../../utils/gradeUtils'
import { ReportCard, ToastNotification, OverviewTextarea, UpdateButton } from './CourseReportComponents'
import { getHeaderClass, sortLevels } from './CourseReportUtils'
import style from './CourseReportStyles.module.css'

export const CourseLessonCalculatedGpaReport = ({ studentId, isStudent = false }) => {
	const [data, setData] = useState({})
	const [departments, setDepartments] = useState([])
	const [levels, setLevels] = useState([])
	const [overview, setOverview] = useState({})
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	useEffect(() => {
		loadData()
	}, [studentId])

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const loadData = async () => {
		const res = await getGpaPercentAverages(studentId)
		const overviewRes = await getOverviewTexts(studentId)
		const dataMap = {}
		const deptSet = new Set()
		const levelSet = new Set()

		res.forEach((item) => {
			const { department, studentId, ...levelData } = item
			deptSet.add(department)
			dataMap[department] = {}

			Object.entries(levelData).forEach(([level, value]) => {
				levelSet.add(level)
				dataMap[department][level] = convertToFourPointScale(value ?? 0)
			})
		})

		const overviewMap = {}
		overviewRes.forEach((item) => {
			overviewMap[item.type] = item.desc
		})

		setData(dataMap)
		setDepartments([...deptSet])
		setLevels(['Overall', ...sortLevels([...levelSet].filter((l) => l !== 'Overall'))])
		setOverview(overviewMap)
	}

	const handleOverviewChange = (value) => {
		setOverview((prev) => ({ ...prev, reportCavgpa: value }))
	}

	const handleUpdate = async () => {
		await saveOverviewTexts(studentId, { reportCavgpa: overview.reportCavgpa || '' })
		showToastMessage('Update successful!')
		loadData()
	}

	const getAvg = (value) => (value != null ? parseFloat(value).toFixed(1) : '0.0')

	return (
		<ReportCard title="Calculated GPA">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />

			<table className="table table-bordered text-center">
				<thead>
					<tr>
						<th style={{ width: '150px' }}>Department</th>
						{levels.map((lvl) => (
							<th key={lvl} className={style[getHeaderClass(lvl)]}>
								{lvl}
							</th>
						))}
						<th style={{ width: '400px' }}>Overview</th>
					</tr>
				</thead>
				<tbody>
					{departments.map((dept, index) => (
						<tr key={dept}>
							<td>{dept}</td>
							{levels.map((lvl) => (
								<td key={lvl} className={data[dept]?.[lvl] > 0 ? style[getHeaderClass(lvl)] : ''}>
									{getAvg(data[dept]?.[lvl])}
								</td>
							))}
							{index === 0 && (
								<td rowSpan={departments.length + 1} className={style.overviewCell}>
									<OverviewTextarea
										value={overview.reportCavgpa || ''}
										onChange={handleOverviewChange}
										rows={departments.length * 2}
										isStudent={isStudent}
									/>
								</td>
							)}
						</tr>
					))}
					<tr>
						<td>
							<strong>Average</strong>
						</td>
						{levels.map((lvl) => {
							let avg = 0
							if (lvl === 'Overall') {
								const values = []
								departments.forEach((dept) => {
									const val = parseFloat(data[dept]?.[lvl]) || 0
									if (val > 0) values.push(val)
								})
								avg = values.length > 0 ? (values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(1) : '0.0'
							} else {
								const values = departments.map((dept) => parseFloat(data[dept]?.[lvl]) || 0).filter((val) => val > 0)
								avg = values.length > 0 ? (values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(1) : '0.0'
							}

							return (
								<td key={lvl} className={(avg > 0 ? style[getHeaderClass(lvl)] : '') + ' fw-bold'}>
									{avg}
								</td>
							)
						})}
					</tr>
				</tbody>
			</table>

			<UpdateButton onClick={handleUpdate} isStudent={isStudent} />
		</ReportCard>
	)
}
